import React, { useEffect, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

// Configure pdf.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js`;

interface PDFPreviewProps {
  pdfUrl: string;
  width?: number;
}

interface PagePreview {
  imageUrl: string;
  pageNumber: number;
}

const PDFPreview: React.FC<PDFPreviewProps> = ({ pdfUrl, width = 800 }) => {
  const [pages, setPages] = useState<PagePreview[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const convertPdfToImages = async () => {
      try {
        setLoading(true);
        // Load the PDF document
        const loadingTask = pdfjsLib.getDocument(pdfUrl);
        const pdf = await loadingTask.promise;
        
        // Set total pages
        setTotalPages(pdf.numPages);
        
        // Convert each page
        const pagePromises = Array.from(
          { length: pdf.numPages },
          async (_, index) => {
            const pageNumber = index + 1;
            const page = await pdf.getPage(pageNumber);

            // Calculate height to maintain aspect ratio
            const viewport = page.getViewport({ scale: 1 });
            const scale = width / viewport.width;
            const scaledViewport = page.getViewport({ scale });

            // Prepare canvas
            const canvas = document.createElement('canvas');
            canvas.width = scaledViewport.width;
            canvas.height = scaledViewport.height;
            
            const context = canvas.getContext('2d');
            if (!context) {
              throw new Error('Could not get canvas context');
            }

            const renderContext = {
              canvasContext: context,
              viewport: scaledViewport,
            };

            await page.render(renderContext).promise;

            // Convert canvas to image URL
            const imageUrl = canvas.toDataURL('image/png');
            return { imageUrl, pageNumber };
          }
        );

        // Wait for all pages to be converted
        const pageResults = await Promise.all(pagePromises);
        
        // Sort pages by page number
        const sortedPages = pageResults.sort((a, b) => a.pageNumber - b.pageNumber);
        setPages(sortedPages);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to convert PDF');
      } finally {
        setLoading(false);
      }
    };

    if (pdfUrl) {
      convertPdfToImages();
    }
  }, [pdfUrl, width]);

  if (error) {
    return <div className="text-red-500">Error: {error}</div>;
  }

  return (
    <div className="space-y-4">
      {loading ? (
        <div>
          <div className="animate-pulse bg-gray-200 rounded-lg" 
               style={{ width, height: width * 1.414 }}>
            Loading PDF pages...
          </div>
        </div>
      ) : (
        <>
          <div className="text-sm text-gray-500">
            Total Pages: {totalPages}
          </div>
          {pages.map((page) => (
            <div key={page.pageNumber} className="relative">
              <div className="absolute top-2 right-2 bg-gray-800 text-white px-2 py-1 rounded-md text-sm">
                Page {page.pageNumber}
              </div>
              <img 
                src={page.imageUrl} 
                alt={`PDF page ${page.pageNumber}`}
                className="max-w-full h-auto rounded-lg shadow-lg"
              />
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default PDFPreview;